/**
 * Epic AB#1824 — Top-level Business Hours Matrix.
 *
 * Replaces the legacy BusinessHoursEditor on `/my-business/business-details`.
 * Owns the state machine flowing between four modes:
 *
 *   INITIAL_LOAD → §A/B/C render
 *   USER_EDITS_DEFAULT_CELL → DRAFT_DIRTY → Save click → BulkApplyConfirmModal
 *   USER_EDITS_BRANCH_CELL  → BRANCH_DRAFT_DIRTY → inline Save (no modal)
 *   USER_CLICKS_REVERT      → inline confirm → server revert
 *
 * 409 Hours.Stale dispatches to the ConcurrencyBanner; the matrix marks
 * conflicted cells as stale and the Save button is disabled until the user
 * acts on the banner.
 *
 * Hook order: every `useState` / `useCallback` lives at the top of the
 * function (before any early-return). Conditional UI (loading / error /
 * empty / matrix) is handled via a render switch at the bottom so the
 * hook order stays stable across renders (rules-of-hooks safety).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import {
    useEffectiveBranchHoursQuery,
    useSetBusinessHoursBulkMutation,
    isHoursStaleError,
} from '../../hooks/useBusinessHoursMatrix';
import {
    adaptResponseToMatrixState,
    buildBulkRequestForBranch,
    buildBulkRequestForBulkApply,
    cellValuesEqual,
    DAY_SHORT_KEY,
    DAY_LONG_KEY,
    dirtyBranchIds,
    discardDraft,
    formatCellTime,
    getDefaultCellForDay,
    hasDirtyCells,
    markStaleFromConflict,
    setBranchCellsToDefault,
    updateBranchCell,
    validateCellValue,
    WEEK_DAYS_ORDER,
    type CellValue,
    type MatrixState,
    type BranchState,
    type CellState,
} from './matrixTypes';
import MatrixDayCell from './MatrixDayCell';
import BulkApplyConfirmModal from './BulkApplyConfirmModal';
import ConcurrencyBanner from './ConcurrencyBanner';
import { interpolate, pluralize } from './interpolate';
import styles from './BusinessHoursMatrix.module.css';

export interface BusinessHoursMatrixProps {
    /** Salon display name for the header subtitle. */
    salonName?: string;
}

/** Builds the `id` attribute used on per-branch row headers for deep-linking. */
const branchRowAnchorId = (branchId: number) =>
    `business-hours-matrix-branch-${branchId}`;

const BusinessHoursMatrix: React.FC<BusinessHoursMatrixProps> = ({ salonName }) => {
    const { t, isRTL } = useLanguage();
    const { showToast } = useToast();
    const location = useLocation();

    const { data: response, isLoading, isError, refetch } = useEffectiveBranchHoursQuery();
    const bulkMutation = useSetBusinessHoursBulkMutation(response?.businessId ?? null);

    /* ── Hooks: state + derived values ────────────────────────────── */
    const [state, setState] = useState<MatrixState | null>(null);
    const [forceDivergentView, setForceDivergentView] = useState(false);
    const [editingCell, setEditingCell] = useState<{ branchId: number; dow: number } | null>(null);
    const [revertingCell, setRevertingCell] = useState<{ branchId: number; dow: number } | null>(null);
    const [conflict, setConflict] = useState<{
        details: import('../../types/api-responses').HoursStaleErrorDetails;
        branchName?: string;
    } | null>(null);
    const [bulkModalState, setBulkModalState] = useState<{
        editedDows: number[];
        newValuesByDow: Map<number, CellValue>;
        oldValuesByDow: Map<number, CellValue>;
    } | null>(null);
    const [defaultRowDraft, setDefaultRowDraft] = useState<Map<number, CellValue>>(new Map());

    /**
     * AB#1824 polish — arrow-key matrix navigation.
     *
     * Map keyed by `${rowKey}-${dow}` where `rowKey` is `'default'` for the
     * synthetic Default row OR the branch ID string for per-branch rows.
     * Each `MatrixDayCell` registers its button element via the
     * `cellButtonRef` callback prop; the cell un-registers on unmount by
     * passing `null`. The resolver below maps an arrow key on a focused
     * cell to its neighbour in the grid and calls `.focus()` on the
     * neighbour. Left/right respects `displayDows` (which is reversed in
     * RTL) so visual direction matches keyboard direction in AR.
     */
    const cellRefMapRef = useRef<Map<string, HTMLButtonElement>>(new Map());
    const setCellRef = useCallback(
        (rowKey: string, dow: number) => (el: HTMLButtonElement | null) => {
            const key = `${rowKey}-${dow}`;
            if (el) cellRefMapRef.current.set(key, el);
            else cellRefMapRef.current.delete(key);
        },
        [],
    );

    // Seed the matrix state from the server response.
    useEffect(() => {
        if (response) {
            setState(adaptResponseToMatrixState(response));
        }
    }, [response]);

    // Deep-link: scroll to branch row when route hash matches anchor.
    useEffect(() => {
        if (!state) return;
        const hash = location.hash?.replace(/^#/, '');
        if (!hash) return;
        const el = document.getElementById(hash);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [state, location.hash]);

    /* ── Hook: per-branch save (used by inline cell + revert + bulk) ── */
    const handlePerBranchSave = useCallback(async (branchId: number) => {
        if (!state) return;
        const branch = state.branches.find((b) => b.branchId === branchId);
        if (!branch) return;
        const request = buildBulkRequestForBranch(state, branchId);
        if (request.branches[0]?.days.length === 0) return;
        try {
            await bulkMutation.mutateAsync(request);
            const firstDow = request.branches[0].days[0].dayOfWeek;
            showToast(
                interpolate(t('hoursMatrix.toast.success.singleBranch'), {
                    branch: branch.branchName,
                    day: t(DAY_LONG_KEY(firstDow)),
                }),
                'success',
            );
        } catch (err: unknown) {
            if (isHoursStaleError(err)) {
                const details = err.details;
                setState((s) => (s ? markStaleFromConflict(s, details) : s));
                setConflict({ details, branchName: branch.branchName });
                return;
            }
            showToast(t('hoursMatrix.toast.error.generic'), 'error');
        }
    }, [state, bulkMutation, showToast, t]);

    /* ── Hook: cell handlers ──────────────────────────────────────── */
    const handleEditCell = useCallback((branchId: number | 'default', dow: number) => {
        setEditingCell(branchId === 'default'
            ? { branchId: 0, dow } // 0 = default-row sentinel
            : { branchId, dow });
        setRevertingCell(null);
    }, []);

    const handleCancelEdit = useCallback(() => {
        setEditingCell(null);
    }, []);

    const handleSubmitCell = useCallback((branchId: number | 'default', dow: number, next: CellValue) => {
        const v = validateCellValue(next);
        if (!v.ok) {
            showToast(v.error ?? t('hoursMatrix.toast.error.validation'), 'error');
            return;
        }
        if (branchId === 'default') {
            setDefaultRowDraft((prev) => {
                const next2 = new Map(prev);
                next2.set(dow, next);
                return next2;
            });
        } else {
            setState((s) => (s ? updateBranchCell(s, branchId, dow, next) : s));
        }
        setEditingCell(null);
    }, [showToast, t]);

    /* ── Hook: Bulk-apply modal flow ───────────────────────────── */
    const openBulkModal = useCallback(() => {
        if (!state) return;
        if (defaultRowDraft.size === 0 && !hasDirtyCells(state)) return;
        if (defaultRowDraft.size === 0) {
            const dirtyBranches = dirtyBranchIds(state);
            if (dirtyBranches.length === 0) return;
            Promise.all(dirtyBranches.map(handlePerBranchSave));
            return;
        }
        const editedDows = Array.from(defaultRowDraft.keys()).sort((a, b) => a - b);
        const oldValuesByDow = new Map<number, CellValue>();
        editedDows.forEach((dow) => {
            const def = getDefaultCellForDay(state, dow);
            const fallback = state.branches[0]?.cells[dow] as CellValue | undefined;
            oldValuesByDow.set(dow, def.value ?? fallback ?? {
                isOpen: false, openTime: null, closeTime: null, is24Hours: false,
            });
        });
        setBulkModalState({
            editedDows,
            newValuesByDow: new Map(defaultRowDraft),
            oldValuesByDow,
        });
    }, [defaultRowDraft, state, handlePerBranchSave]);

    const handleBulkConfirm = useCallback(async (selectedBranchIds: number[]) => {
        if (!bulkModalState || !state) return;
        const request = buildBulkRequestForBulkApply(
            state,
            new Set(selectedBranchIds),
            new Set(bulkModalState.editedDows),
            bulkModalState.newValuesByDow,
        );
        if (request.branches.length === 0) {
            setBulkModalState(null);
            return;
        }
        try {
            await bulkMutation.mutateAsync(request);
            const k = selectedBranchIds.length;
            if (bulkModalState.editedDows.length === 1) {
                showToast(
                    pluralize(t, 'hoursMatrix.toast.success.bulk', k, {
                        day: t(DAY_LONG_KEY(bulkModalState.editedDows[0])),
                        k,
                    }),
                    'success',
                );
            } else {
                showToast(
                    pluralize(t, 'hoursMatrix.toast.success.bulkGeneric', k, { k }),
                    'success',
                );
            }
            setDefaultRowDraft(new Map());
            setBulkModalState(null);
            setForceDivergentView(false);
        } catch (err: unknown) {
            if (isHoursStaleError(err)) {
                const details = err.details;
                setState((s) => (s ? markStaleFromConflict(s, details) : s));
                setConflict({ details });
                setBulkModalState(null);
                return;
            }
            const axiosErr = err as { response?: { status?: number; data?: { errors?: Array<{ code?: string }> } } };
            const status = axiosErr?.response?.status;
            const code = axiosErr?.response?.data?.errors?.[0]?.code;
            if (status === 404 && code === 'Branch.NotFound') {
                showToast(t('hoursMatrix.toast.error.branchNotFound'), 'error');
                refetch();
                setBulkModalState(null);
                return;
            }
            showToast(t('hoursMatrix.toast.error.generic'), 'error');
        }
    }, [bulkModalState, state, bulkMutation, showToast, t, refetch]);

    /* ── Hook: revert flow ────────────────────────────────────────── */
    const handleRequestRevert = useCallback((branchId: number, dow: number) => {
        setRevertingCell({ branchId, dow });
        setEditingCell(null);
    }, []);

    const handleConfirmRevert = useCallback(async () => {
        if (!revertingCell || !state) return;
        const branch = state.branches.find((b) => b.branchId === revertingCell.branchId);
        if (!branch) return;
        const next = setBranchCellsToDefault(state, revertingCell.branchId, revertingCell.dow);
        const req = buildBulkRequestForBranch(next, revertingCell.branchId);
        try {
            await bulkMutation.mutateAsync(req);
            showToast(
                interpolate(t('hoursMatrix.toast.revertSuccess'), {
                    branch: branch.branchName,
                    day: t(DAY_LONG_KEY(revertingCell.dow)),
                }),
                'success',
            );
            setRevertingCell(null);
        } catch (err: unknown) {
            if (isHoursStaleError(err)) {
                const details = err.details;
                setState((s) => (s ? markStaleFromConflict(s, details) : s));
                setConflict({ details, branchName: branch.branchName });
                setRevertingCell(null);
                return;
            }
            showToast(t('hoursMatrix.toast.error.generic'), 'error');
        }
    }, [revertingCell, state, bulkMutation, showToast, t]);

    /* ── Hook: Save / Cancel (header) ─────────────────────────────── */
    const handleHeaderSave = useCallback(() => {
        if (!state) return;
        if (defaultRowDraft.size > 0) {
            openBulkModal();
            return;
        }
        const dirtyBranches = dirtyBranchIds(state);
        if (dirtyBranches.length === 0) return;
        Promise.all(dirtyBranches.map(handlePerBranchSave));
    }, [defaultRowDraft.size, openBulkModal, state, handlePerBranchSave]);

    const handleCancel = useCallback(() => {
        setState((s) => (s ? discardDraft(s) : s));
        setDefaultRowDraft(new Map());
        setEditingCell(null);
        setRevertingCell(null);
    }, []);

    /* ── Hook: Concurrency banner actions ─────────────────────────── */
    const handleReviewConflict = useCallback(() => {
        refetch();
        setConflict(null);
    }, [refetch]);

    const handleDiscardConflict = useCallback(() => {
        setState((s) => (s ? discardDraft(s) : s));
        setDefaultRowDraft(new Map());
        setConflict(null);
    }, []);

    /* ── Hook: derived view mode ─────────────────────────────────── */
    const viewMode: 'single' | 'uniform' | 'divergent' = useMemo(() => {
        if (!state) return 'single';
        if (state.branches.length === 1) return 'single';
        return state.divergence === 'uniform' && !forceDivergentView ? 'uniform' : 'divergent';
    }, [state, forceDivergentView]);

    const divergeCount = useMemo(() => {
        if (!state) return 0;
        return WEEK_DAYS_ORDER.reduce((n, dow) => {
            const { differsCount } = getDefaultCellForDay(state, dow);
            return n + (differsCount > 0 ? 1 : 0);
        }, 0);
    }, [state]);

    const displayDows = useMemo(
        () => (isRTL ? [...WEEK_DAYS_ORDER].slice().reverse() : WEEK_DAYS_ORDER),
        [isRTL],
    );

    /**
     * AB#1824 polish — arrow-key matrix navigation (Omar N1, fast-follow).
     * Resolves the neighbour of `(rowKey, dow)` in `direction` and focuses
     * its registered button via the `cellRefMapRef` map populated by each
     * cell's `cellButtonRef` callback. Row order is Default → branches in
     * server order. `displayDows` is the visual order (reversed in RTL) so
     * Left/Right always means "next visual cell", consistent with how the
     * user sees the matrix.
     *
     * Hook is declared ABOVE the early-return on `!state` so the hook
     * order stays stable across the loading → ready transition (rules of
     * hooks). The callback itself short-circuits when `state` is null,
     * which can happen on the first render after a refetch.
     */
    const handleArrowKey = useCallback(
        (rowKey: string, dow: number) => (direction: 'left' | 'right' | 'up' | 'down' | 'home' | 'end') => {
            if (!state) return;
            const dowIdx = displayDows.indexOf(dow);
            const rowOrder: string[] = ['default'];
            const isDivergent = state.divergence !== 'uniform';
            if (isDivergent) state.branches.forEach((b) => rowOrder.push(String(b.branchId)));
            const rowIdx = rowOrder.indexOf(rowKey);
            let next: { rowKey: string; dow: number } | null = null;
            switch (direction) {
                case 'left':
                    if (dowIdx > 0) next = { rowKey, dow: displayDows[dowIdx - 1] };
                    break;
                case 'right':
                    if (dowIdx < displayDows.length - 1) next = { rowKey, dow: displayDows[dowIdx + 1] };
                    break;
                case 'up':
                    if (rowIdx > 0) next = { rowKey: rowOrder[rowIdx - 1], dow };
                    break;
                case 'down':
                    if (rowIdx < rowOrder.length - 1) next = { rowKey: rowOrder[rowIdx + 1], dow };
                    break;
                case 'home':
                    next = { rowKey, dow: displayDows[0] };
                    break;
                case 'end':
                    next = { rowKey, dow: displayDows[displayDows.length - 1] };
                    break;
            }
            if (!next) return;
            const key = `${next.rowKey}-${next.dow}`;
            cellRefMapRef.current.get(key)?.focus();
        },
        [state, displayDows],
    );

    /* ── Early-return UI states (rendered as JSX, no extra hooks) ── */
    if (isLoading) {
        return (
            <div className={styles.placeholder}>
                <div className={styles.placeholderText}>{t('hoursMatrix.loading')}</div>
            </div>
        );
    }
    if (isError || !state) {
        return (
            <div className={`${styles.placeholder} ${styles.placeholderError}`} role="alert">
                <div className={styles.placeholderIcon} aria-hidden="true">⚠️</div>
                <div className={styles.placeholderText}>{t('hoursMatrix.error.fetch')}</div>
                <button type="button" className={styles.btnGhost} onClick={() => refetch()}>
                    {t('hoursMatrix.error.retry')}
                </button>
            </div>
        );
    }
    if (state.branches.length === 0) {
        return (
            <div className={styles.placeholder}>
                <div className={styles.placeholderIcon} aria-hidden="true">📍</div>
                <div className={styles.placeholderText}>
                    {t('hoursMatrix.emptyState.noBranches')}
                </div>
            </div>
        );
    }

    /* ── Render helpers ──────────────────────────────────────────── */
    const subtitle = (() => {
        const tokens: Record<string, string | number> = {
            salonName: salonName ?? '',
            n: state.branches.length,
            k: divergeCount,
        };
        if (viewMode === 'single') return interpolate(t('hoursMatrix.subtitle.single'), tokens);
        if (viewMode === 'uniform') return interpolate(t('hoursMatrix.subtitle.uniform'), tokens);
        return interpolate(t('hoursMatrix.subtitle.divergent'), tokens);
    })();

    const headerChip = (() => {
        if (viewMode === 'uniform') {
            return <span className={styles.uniformChip}>{t('hoursMatrix.uniform.chip')}</span>;
        }
        if (viewMode === 'divergent' && divergeCount > 0) {
            return (
                <span className={styles.divergenceChip}>
                    {pluralize(t, 'hoursMatrix.divergence.chip', divergeCount, { k: divergeCount })}
                </span>
            );
        }
        return null;
    })();

    const isDirty = hasDirtyCells(state);
    const canSave = (isDirty || defaultRowDraft.size > 0) && !conflict && !bulkMutation.isPending;
    const showDefaultRow = true;
    const showBranchRows = viewMode === 'divergent';

    const defaultDisplayValue = (dow: number): CellValue => {
        const draft = defaultRowDraft.get(dow);
        if (draft) return draft;
        const def = getDefaultCellForDay(state, dow);
        return def.value ?? { isOpen: false, openTime: null, closeTime: null, is24Hours: false };
    };

    const renderBranchesStrip = () => {
        if (viewMode !== 'uniform') return null;
        return (
            <button
                type="button"
                className={`${styles.branchesStrip} ${forceDivergentView ? styles.branchesStripOpen : ''}`}
                onClick={() => setForceDivergentView((v) => !v)}
                aria-expanded={forceDivergentView}
                aria-label={
                    forceDivergentView
                        ? t('hoursMatrix.branchesStrip.collapseHint')
                        : t('hoursMatrix.branchesStrip.expandHint')
                }
            >
                <span className={styles.chev}>▸</span>
                <span className={styles.branchesStripLabel}>
                    {state.branches.length === 1
                        ? interpolate(t('hoursMatrix.branchesStrip.label.one'), {
                            n: state.branches.length,
                        })
                        : interpolate(t('hoursMatrix.branchesStrip.label.other'), {
                            n: state.branches.length,
                        })}
                </span>
                {state.branches.map((branch) => (
                    <span key={branch.branchId} className={styles.branchPill}>
                        {branch.branchName}
                        {branch.isPrimary && (
                            <span className={styles.branchPillHq}>
                                {t('hoursMatrix.branch.hq')}
                            </span>
                        )}
                    </span>
                ))}
            </button>
        );
    };

    const renderColHeads = () => (
        <>
            <div className={`${styles.colHead} ${styles.colHeadRowLabel}`} role="columnheader">
                {t('hoursMatrix.column.branch')}
            </div>
            {displayDows.map((dow) => (
                <div key={dow} className={styles.colHead} role="columnheader">
                    {t(DAY_SHORT_KEY(dow))}
                </div>
            ))}
        </>
    );

    const renderDefaultRow = () => {
        const pillKey = viewMode === 'single'
            ? 'hoursMatrix.defaultRow.pill.single'
            : viewMode === 'uniform'
                ? 'hoursMatrix.defaultRow.pill.uniform'
                : 'hoursMatrix.defaultRow.pill.divergent';
        const subKey = viewMode === 'single'
            ? 'hoursMatrix.defaultRow.sub.single'
            : viewMode === 'uniform'
                ? 'hoursMatrix.defaultRow.sub.uniform'
                : (divergeCount === 1
                    ? 'hoursMatrix.defaultRow.sub.divergent.one'
                    : 'hoursMatrix.defaultRow.sub.divergent.other');
        const subText = interpolate(t(subKey), {
            n: state.branches.length,
            k: divergeCount,
        });
        return (
            <React.Fragment key="default-row">
                <div
                    className={`${styles.rowLabel} ${styles.rowLabelDefault}`}
                    role="rowheader"
                    aria-label={t('hoursMatrix.a11y.defaultRow')}
                >
                    <div className={styles.rowLabelName}>
                        <span>{t('hoursMatrix.defaultRow.label')}</span>
                        <span className={styles.defaultPill}>{t(pillKey)}</span>
                    </div>
                    <div
                        className={`${styles.rowLabelSub} ${
                            viewMode === 'divergent' && divergeCount > 0 ? styles.rowLabelSubWarn : ''
                        }`}
                    >
                        {subText}
                    </div>
                </div>
                {displayDows.map((dow) => {
                    const value = defaultDisplayValue(dow);
                    const isEditing =
                        editingCell?.branchId === 0 && editingCell?.dow === dow;
                    const { differsCount } = getDefaultCellForDay(state, dow);
                    const isDraftDirty = defaultRowDraft.has(dow);
                    const renderCell: CellState = {
                        dayOfWeek: dow,
                        ...value,
                        serverValue: getDefaultCellForDay(state, dow).value ?? value,
                        serverUpdatedAt: null,
                        isDirty: isDraftDirty,
                        isStale: false,
                    };
                    const fmt = formatCellTime(value);
                    const ariaLabel = interpolate(
                        t(fmt.kind === 'open' ? 'hoursMatrix.a11y.cell.open' : 'hoursMatrix.a11y.cell.closed'),
                        {
                            day: t(DAY_LONG_KEY(dow)),
                            branch: t('hoursMatrix.defaultRow.label'),
                            open: value.openTime ?? '',
                            close: value.closeTime ?? '',
                        },
                    );
                    return (
                        <MatrixDayCell
                            key={`default-${dow}`}
                            cell={renderCell}
                            branchName={t('hoursMatrix.defaultRow.label')}
                            defaultValue={null}
                            isDefaultRow
                            isDivergentChip={false}
                            differsCount={viewMode === 'divergent' ? differsCount : 0}
                            isEditing={isEditing}
                            onRequestEdit={() => handleEditCell('default', dow)}
                            onSubmit={(next) => handleSubmitCell('default', dow, next)}
                            onCancelEdit={handleCancelEdit}
                            canRevert={false}
                            onRequestRevert={() => { /* no-op for default row */ }}
                            onArrowKey={handleArrowKey('default', dow)}
                            cellButtonRef={setCellRef('default', dow)}
                            isLastRow={!showBranchRows}
                            ariaLabel={ariaLabel}
                        />
                    );
                })}
            </React.Fragment>
        );
    };

    const renderBranchRow = (
        branch: BranchState,
        isLastBranch: boolean,
    ) => {
        const branchDivergeCount = branch.cells.reduce((n, cell) => {
            const def = getDefaultCellForDay(state, cell.dayOfWeek).value;
            if (!def) return n;
            return n + (cellValuesEqual(cell, def) ? 0 : 1);
        }, 0);
        const branchAriaLabel = interpolate(t('hoursMatrix.a11y.branchRow'), {
            branch: branch.branchName,
            k: branchDivergeCount,
        });
        return (
            <React.Fragment key={`branch-${branch.branchId}`}>
                <div
                    id={branchRowAnchorId(branch.branchId)}
                    className={styles.rowLabel}
                    role="rowheader"
                    aria-label={branchAriaLabel}
                >
                    <div className={styles.rowLabelName}>
                        <span>{branch.branchName}</span>
                        {branch.isPrimary && (
                            <span className={styles.hqPill}>{t('hoursMatrix.branch.hq')}</span>
                        )}
                    </div>
                    {branchDivergeCount > 0 && (
                        <button
                            type="button"
                            className={styles.revertLink}
                            onClick={() => {
                                let next = state;
                                branch.cells.forEach((c) => {
                                    const def = getDefaultCellForDay(state, c.dayOfWeek).value;
                                    if (def && !cellValuesEqual(c, def)) {
                                        next = setBranchCellsToDefault(next, branch.branchId, c.dayOfWeek);
                                    }
                                });
                                setState(next);
                                setTimeout(() => handlePerBranchSave(branch.branchId), 0);
                            }}
                        >
                            {t('hoursMatrix.branch.revertAllLink')}
                        </button>
                    )}
                </div>
                {displayDows.map((dow) => {
                    const cell = branch.cells.find((c) => c.dayOfWeek === dow);
                    if (!cell) return null;
                    const defaultValue = getDefaultCellForDay(state, dow).value;
                    const matchesDefault =
                        defaultValue !== null && cellValuesEqual(cell, defaultValue);
                    const isDivergent =
                        defaultValue !== null && !matchesDefault;
                    const isEditing =
                        editingCell?.branchId === branch.branchId && editingCell?.dow === dow;
                    const isReverting =
                        revertingCell?.branchId === branch.branchId && revertingCell?.dow === dow;
                    const fmt = formatCellTime(cell);
                    const ariaLabel = matchesDefault
                        ? interpolate(t('hoursMatrix.a11y.cell.matchesDefault'), {
                            day: t(DAY_LONG_KEY(dow)),
                            branch: branch.branchName,
                        })
                        : interpolate(
                            t(isDivergent ? 'hoursMatrix.a11y.cell.divergent'
                                : fmt.kind === 'open' ? 'hoursMatrix.a11y.cell.open' : 'hoursMatrix.a11y.cell.closed'),
                            {
                                day: t(DAY_LONG_KEY(dow)),
                                branch: branch.branchName,
                                open: cell.openTime ?? '',
                                close: cell.closeTime ?? '',
                            },
                        );

                    if (isReverting) {
                        const newValueText = defaultValue
                            ? (formatCellTime(defaultValue).kind === 'open'
                                ? formatCellTime(defaultValue).text
                                : t('hoursMatrix.cell.closed'))
                            : t('hoursMatrix.cell.closed');
                        return (
                            <div
                                key={`branch-${branch.branchId}-${dow}`}
                                className={`${styles.dayCell} ${styles.dayCellEditing} ${isLastBranch ? styles.dayCellLastRow : ''}`}
                                role="gridcell"
                            >
                                <div className={styles.revertConfirm}>
                                    <div className={styles.revertConfirmText}>
                                        {interpolate(t('hoursMatrix.revertConfirm.text'), {
                                            branch: branch.branchName,
                                            day: t(DAY_LONG_KEY(dow)),
                                            newValue: newValueText,
                                        })}
                                    </div>
                                    <div className={styles.revertConfirmActions}>
                                        <button
                                            type="button"
                                            className={styles.revertConfirmApply}
                                            onClick={handleConfirmRevert}
                                        >
                                            {t('hoursMatrix.revertConfirm.apply')}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.revertConfirmCancel}
                                            onClick={() => setRevertingCell(null)}
                                        >
                                            {t('hoursMatrix.revertConfirm.cancel')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <MatrixDayCell
                            key={`branch-${branch.branchId}-${dow}`}
                            cell={cell}
                            branchName={branch.branchName}
                            defaultValue={defaultValue}
                            isDefaultRow={false}
                            isDivergentChip={isDivergent}
                            differsCount={0}
                            isEditing={isEditing}
                            onRequestEdit={() => handleEditCell(branch.branchId, dow)}
                            onSubmit={(next) => {
                                handleSubmitCell(branch.branchId, dow, next);
                                // Auto-fire per-branch save on inline edit (Dina §2.5).
                                setTimeout(() => handlePerBranchSave(branch.branchId), 0);
                            }}
                            onCancelEdit={handleCancelEdit}
                            canRevert={isDivergent}
                            onRequestRevert={() => handleRequestRevert(branch.branchId, dow)}
                            onArrowKey={handleArrowKey(String(branch.branchId), dow)}
                            cellButtonRef={setCellRef(String(branch.branchId), dow)}
                            isLastRow={isLastBranch}
                            ariaLabel={ariaLabel}
                        />
                    );
                })}
            </React.Fragment>
        );
    };

    const footerHint = (() => {
        if (conflict) return t('hoursMatrix.foot.staleHint');
        if (viewMode === 'single') {
            return interpolate(t('hoursMatrix.foot.singleHint'), {
                salonName: salonName ?? state.branches[0]?.branchName ?? '',
            });
        }
        if (viewMode === 'uniform') {
            return interpolate(t('hoursMatrix.foot.uniformHint'), {
                n: state.branches.length,
            });
        }
        return t('hoursMatrix.foot.divergentHint');
    })();

    return (
        <div className={styles.matrixShell}>
            <div className={styles.matrixHeader}>
                <h4>{t('hoursMatrix.title')}</h4>
                <span className={styles.matrixHeaderMeta}>
                    {subtitle}
                    {headerChip}
                </span>
                <span className={styles.spacer} />
                <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={handleCancel}
                    disabled={!isDirty && defaultRowDraft.size === 0}
                >
                    {t('hoursMatrix.actions.cancel')}
                </button>
                <button
                    type="button"
                    className={styles.btnPrimary}
                    onClick={handleHeaderSave}
                    disabled={!canSave}
                >
                    {bulkMutation.isPending
                        ? t('hoursMatrix.actions.saving')
                        : t('hoursMatrix.actions.save')}
                </button>
            </div>

            {conflict && (
                <ConcurrencyBanner
                    details={conflict.details}
                    branchName={conflict.branchName}
                    onReview={handleReviewConflict}
                    onDiscard={handleDiscardConflict}
                    onDismiss={() => setConflict(null)}
                />
            )}

            <div className={styles.matrix}>
                <div
                    className={`${styles.matrixGrid} ${isRTL ? styles.matrixGridRtl : ''}`}
                    role="grid"
                    aria-label={interpolate(t('hoursMatrix.a11y.grid'), {
                        n: state.branches.length,
                    })}
                >
                    {renderColHeads()}
                    {showDefaultRow && renderDefaultRow()}
                    {showBranchRows && state.branches.map((br, idx) =>
                        renderBranchRow(br, idx === state.branches.length - 1),
                    )}
                </div>

                {renderBranchesStrip()}

                <div className={styles.matrixFoot}>
                    <span className={styles.draftMeta}>{footerHint}</span>
                </div>
            </div>

            {bulkModalState && (
                <BulkApplyConfirmModal
                    editedDows={bulkModalState.editedDows}
                    newValuesByDow={bulkModalState.newValuesByDow}
                    oldValuesByDow={bulkModalState.oldValuesByDow}
                    branches={state.branches}
                    onConfirm={handleBulkConfirm}
                    onCancel={() => setBulkModalState(null)}
                    isSaving={bulkMutation.isPending}
                />
            )}
        </div>
    );
};

export default BusinessHoursMatrix;
